<template>
  <div
    class="elements-preview"
  >
    <Button
      v-if="!showDragIcon"
      class="section-overlay section-insert p-button-success"
      icon="far fa-check"
      :label="loadActionTitle"
      @click="handleSelection(item)"
    />
    <i
      v-else
      class="fas fa-grip-dots-vertical elements-preview__drag-icon"
    />
    <Chip
      style="max-width: 210px;"
      :label="elementLabel"
      :icon="isSync ? 'fas fa-arrows-repeat' : ''"
      class="flex elements-preview__chip"
    />
    <div
      v-if="isSync && templateCount > 0"
      v-tooltip="t('templateBuilder.modals.loadElements.templateCount', { count: templateCount })"
      class="elements-preview__template-count"
    >
      {{ templateCount }}
    </div>
    <SpmOverlayPanel
      v-if="item.type !== 'empty_section'"
      class-trigger="elements-preview__action-menu"
      :close-panel="closeMenuActionsPanel"
    >
      <template #trigger>
        <i class="far fa-ellipsis-v fa-md elements-preview__action-button" />
      </template>
      <SpmPanelMenu
        :items="actionItems"
      />
    </SpmOverlayPanel>
    <div
      class="elements-preview__name"
    >
      {{ item.name }}
    </div>
    <div
      class="elements-preview__image"
    >
      <ProgressSpinner
        style="width: 25px; height: 25px"
        stroke-width="8"
        fill="var(--surface-ground)"
        animation-duration="1.0s"
        aria-label="Custom ProgressSpinner"
        :class="{ block: loadingPreview, hidden: !loadingPreview }"
      />
      <Image
        :key="backgroundImageUrl"
        :src="backgroundImageUrl"
        :image-class="{ block: !loadingPreview, hidden: loadingPreview }"
        @load="onImageLoaded"
        @error="onImageError"
        preview
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  SetupContext,
  watch,
  ref,
  onMounted,
  Ref,
  computed,
} from 'vue';

import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmPanelMenu from '@/components/spm-primevue/SpmPanelMenu.vue';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Chip from 'primevue/chip';
import Tooltip from 'primevue/tooltip';
// eslint-disable-next-line import/extensions,import/no-unresolved
import { MenuItem } from 'primevue/menuitem';

import Image from 'primevue/image';

import {
  MEDIA_URL_PART_THUMB_GENERATION,
  TEMPLATE_PART_ELEMENT_PART_TYPE,
  TEMPLATE_PART_ELEMENT_SUBTYPE,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
} from '@/components/template-builder/utils/constants';

import {
  TemplateEditorState,
  removeItemFromSectionsToRefresh,
} from '@/composables/template-editor/TemplateEditor';

import { UserState } from '@/composables/User';

import { generateSha1HashWithSalt } from '@/helpers';

import {
  TemplatePart,
} from '@/types';

import { useI18n } from 'vue-i18n';
import { getWidgetType, getWidgetLabelTranslationKey } from './utils/helpers';

export default defineComponent({
  name: 'LoadElementsPreview',

  components: {
    Button,
    ProgressSpinner,
    SpmOverlayPanel,
    SpmPanelMenu,
    Chip,
    Image,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    item: {
      type: Object as PropType<TemplatePart>,
      required: true,
    },

    showDragIcon: {
      type: Boolean,
      required: false,
      default: true,
    },

    loadActionTitle: {
      type: String,
      default: '',
    },

    templateCount: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  emits: ['handle-selection', 'handle-deletion', 'handle-rename', 'handle-duplicate'],

  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();

    const getBackgoundImageUrl = () => (props.item.type === 'empty_section'
      ? '/images/template-builder/new_empty_section.png'
      : `${MEDIA_URL_PART_THUMB_GENERATION
        .replace('{ID_SHOP}', String(UserState.activeShop?.id ?? 0))
        .replace('{SHA1_KEY}', generateSha1HashWithSalt(props.item.id_template_parts, 'salt-'))}&t=${new Date().getTime()}`);

    const backgroundImageUrl = ref(getBackgoundImageUrl());

    const maxRetries = ref(30);
    const retryCount = ref(0);
    const loadingPreview = ref(false);
    const closeMenuActionsPanel = ref(false);

    const isSync = computed(() => {
      if (props.item.data) {
        const data = JSON.parse(props.item.data);
        if (data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]) {
          return true;
        }
      }
      return false;
    });

    const elementPartType = computed(() => {
      if (props.item.data) {
        const data = JSON.parse(props.item.data);
        if (data[TEMPLATE_PART_ELEMENT_PART_TYPE]) {
          return data[TEMPLATE_PART_ELEMENT_PART_TYPE];
        }
      }
      return null;
    });

    const elementSubtype = computed(() => {
      if (props.item.data) {
        const data = JSON.parse(props.item.data);
        if (data[TEMPLATE_PART_ELEMENT_SUBTYPE]) {
          return getWidgetType(data[TEMPLATE_PART_ELEMENT_SUBTYPE]);
        }
      }
      return null;
    });

    const elementLabel = computed(() => {
      if (elementPartType.value) {
        return t(
          `templateBuilder.panels.loadElements.chip.elementPart.${elementPartType.value}`,
          [
            t(
              `templateBuilder.panels.loadElements.chip.elementPart.elements.${props.item.type}`,
              [elementSubtype.value ? t(getWidgetLabelTranslationKey(elementSubtype.value)) : ''],
            ),
          ],
        );
      }
      return t(`templateBuilder.panels.loadElements.chip.${props.item.type}`);
    });

    const reRenderPreview = () => {
      backgroundImageUrl.value = getBackgoundImageUrl();
    };

    const onImageLoaded = () => {
      loadingPreview.value = false;
    };

    const onImageError = () => {
      loadingPreview.value = true;
      // Retry loading image
      if (retryCount.value < maxRetries.value) {
        retryCount.value += 1;
        setTimeout(() => {
          reRenderPreview();
        }, 1000);
      } else {
        loadingPreview.value = false;
      }
    };

    const handleSelection = async (item: TemplatePart) => {
      emit('handle-selection', item);
    };

    const handleDeletion = (item: TemplatePart, event: any) => {
      closeMenuActionsPanel.value = false;
      emit('handle-deletion', item, event, () => {
        closeMenuActionsPanel.value = true;
      });
    };

    const handleRename = (item: TemplatePart, event: any) => {
      closeMenuActionsPanel.value = false;
      emit('handle-rename', item, event, () => {
        closeMenuActionsPanel.value = true;
      });
    };

    const handleDuplicate = (item: TemplatePart, event: any) => {
      closeMenuActionsPanel.value = false;
      emit('handle-duplicate', item, event, () => {
        closeMenuActionsPanel.value = true;
      });
    };

    const checkRefreshPreview = () => {
      if (TemplateEditorState.sectionsToRefresh.find((section) => section === props.item.id_template_parts)) {
        loadingPreview.value = true;
        setTimeout(() => {
          reRenderPreview();
          removeItemFromSectionsToRefresh(props.item.id_template_parts);
        }, 1000);
      }
    };

    const actionItems: Ref<MenuItem[]> = ref([
      {
        label: t('templateBuilder.panels.loadElements.actions.rename'),
        icon: 'far fa-edit',
        command: (event) => {
          handleRename(props.item, event.originalEvent);
        },
      },
      {
        label: t('templateBuilder.panels.loadElements.actions.duplicate'),
        icon: 'far fa-clone',
        command: (event) => {
          handleDuplicate(props.item, event.originalEvent);
        },
      },
      {
        label: t('templateBuilder.panels.loadElements.actions.delete'),
        icon: 'far fa-trash',
        command: (event) => {
          handleDeletion(props.item, event.originalEvent);
        },
      },
    ]);

    watch(() => TemplateEditorState.sectionsToRefresh, () => {
      checkRefreshPreview();
    }, { deep: true });

    onMounted(() => {
      checkRefreshPreview();
    });

    return {
      t,
      backgroundImageUrl,
      loadingPreview,
      actionItems,
      isSync,
      closeMenuActionsPanel,
      elementLabel,

      handleSelection,
      onImageLoaded,
      onImageError,
    };
  },
});
</script>

<style lang="scss" scoped>
.elements-preview {
  border: $template-builder-panels-sections-section-border;
  position: relative;
  cursor: grab;

  &:not(:last-child) {
    margin-bottom: 2rem;
  }

  &:hover {
    .section-overlay {
      display: block;
    }
  }

  .section-overlay {
    display: none;
    position: absolute;

    &.section-insert {
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 50%);
      font-size: 0.875rem;
      padding: 0.25rem 0.875rem;
      z-index: 2;
    }
  }

  &__chip {
    display: block;
    position: absolute;
    margin-top: 0.5rem;
    margin-left: 0.5rem;
    font-size: 12px;
    z-index: 2;
  }

  &__drag-icon {
    position: absolute;
    color: $mid-grey;
    z-index: 2;
    top:22px;
    left:0;
    background:#fff;
    padding-top:75px;
    padding-bottom:75px;
    padding-left:10px;
    padding-right:10px;
  }

  &__name {
    text-align: center;
    font-weight: bold;
    font-size: 0.8em;
    padding: 11px 0px;
  }

  &__image {
    width: 100%;
    height: 150px;
    overflow: hidden;
    justify-content: center;
    align-items: flex-start;
    display: flex;
  }

  &__template-count {
    position: absolute;
    top: 8px;
    right: 35px;
    background: #94c840;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: bold;
    z-index: 3;
    cursor: help;
  }
}
</style>

<style lang="scss">
.elements-preview__image .p-image-preview-indicator {
  /*position:absolute;
  top:75px;
  left:50%;
  right:auto;
  bottom:auto;
  transform:translate(-50%, -50%);*/
  height: 150px;
  width: 100%;
  max-width: 100%;
  object-fit: contain;
  z-index: 1;
}
.elements-preview__image img {
  height: auto;
  width: 100%;
  max-width: 100%;
  object-fit: contain;
}
.elements-preview__action-menu {
  display: block;
  position: absolute;
  right: 5px;
  font-size: 1.2rem;
  padding: 0.1rem 0.1rem !important;
  width: auto !important;
  border: none !important;
  z-index: 2;
  :deep() .p-button-label {
    display: none;
  }
}
.elements-preview__action-button {
  padding: 0.4rem 0.8rem !important;
  margin-top: .2rem;
  border-radius: 4px;
  cursor: pointer;
  width: auto !important;
  border: none !important;
  color: $mid-grey;
  &:hover {
    backdrop-filter: brightness(0.96);
    transition: .2s ease;
  }
}
</style>
